import { Check, FileText, Loader2, Trash2 } from "lucide-react"
import { Form } from "@/components/ui/form"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  FormTextField,
  FormCpfCnpjField,
  FormDateField,
  FormCurrencyField,
  FormTextareaField,
  FormSectionDivider,
  FormSelectField,
  FormFileField,
} from "@/components/form"
import { DialogFormProps, DialogoLancamentoFormData } from "../../relacao-pagamentos.types"
import { useDialogLancamentoForm } from "../../hooks/use-dialog-lancamento-form"
import { fasesOrcamento, historyItems } from "../../constants/relacao-pagamentos.contants"
import { mapDialogFormToPayment } from "../../helpers/relacao-pagamentos-helpers"
import { SelectItem } from "@/components/ui/select"
import { useState } from "react"

export function DialogForm({
  payment,
  isEditing,
  isSaving = false,
  onSave,
  onDelete,
  onClose,
}: DialogFormProps) {
  const {
    form,
    fiscalDocUrl,
    budgetItems,
    handleFileUpload,
    setFaseOrcamento,
    update
  } = useDialogLancamentoForm(payment)

  const handleSubmit = async (data: DialogoLancamentoFormData) => {
    await update({ id: Number(payment?.id ?? 0), data: mapDialogFormToPayment(fiscalDocUrl, data) })
    onClose()
  }

  const handleDelete = () => {
    if (onDelete && confirm("Tem certeza que deseja excluir este lançamento?")) {
      onDelete(payment)
      onClose()
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-6">
        <section className="flex flex-col gap-4">
          <FormSectionDivider title="Identificação do Lançamento" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormTextField name="creditor" label="Credor" required disabled />
            <FormDateField name="payment_date" label="Data de Pagamento" required disabled={!isEditing} />
            <FormTextField name="emission_type" label="Tipo de Documento" required disabled={!isEditing} />
            <FormTextField name="emission_number" label="Nº do Documento" required disabled={!isEditing} />
            <FormDateField name="emission_date" label="Data de Emissão" required disabled={!isEditing} />
            <FormCurrencyField name="amount" label="Valor" disabled={!isEditing} required />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSelectField
              name="fase_orcamento"
              label="Fase do Orçamento"
              placeholder="Selecione a fase do orçamento"
              onChange={(value) => setFaseOrcamento(Number(value))}
              disabled={!isEditing}
              required
            >
              {fasesOrcamento?.map((p, idx) => (
                <SelectItem key={idx} value={p.id.toString()}>{p.step}</SelectItem>
              ))}
            </FormSelectField>

            <FormSelectField
              name="budget_item_id"
              label="Sub-item do Orçamento"
              placeholder="Selecione o sub-item do orçamento"
              disabled={!isEditing}
              required
            >
              {(!budgetItems || budgetItems?.length === 0) && (
                <SelectItem key={0} value="-" disabled>
                  Nenhum sub-item disponível
                </SelectItem>
              )}

              {budgetItems?.map((p, idx) => (
                <SelectItem key={idx} value={p.id.toString()}>
                  {p.description}
                </SelectItem>
              ))}
            </FormSelectField>
          </div>

          <FormFileField
            name="fiscal_note_url"
            label="Upload de Documento Fiscal"
            placeholder="Upload PDF ou XML"
            existingUrl={fiscalDocUrl}
            onFileSelect={(file) => handleFileUpload(file, Number(payment?.id_staff_contract ?? 0))}
            className="md:col-span-2"
            disabled={!isEditing}
          />
        </section>

        <div className="h-px bg-gray-700/50 w-full" />

        {/* Histórico */}
        <div className="flex flex-col gap-3">
          <Label className="text-gray-200 text-sm font-semibold">Histórico</Label>
          <div className="border border-gray-700 rounded-lg p-4 max-h-[160px] overflow-y-auto flex flex-col gap-3">
            {historyItems.map((item, i) => (
              <HistoricoItem key={i} text={item.text} />
            ))}
          </div>
        </div>

        {/* Ações */}
        <div className="flex items-center gap-3 justify-end pt-2">
          {isEditing && (
            <Button
              type="button"
              variant="ghost"
              onClick={handleDelete}
              className="h-10 px-4 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg gap-2 font-medium text-sm"
            >
              <Trash2 className="w-4 h-4" />
              Excluir
            </Button>
          )}

          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="h-10 px-6 border-gray-600 text-gray-300 hover:bg-gray-700/50 rounded-lg font-medium text-sm"
          >
            Cancelar
          </Button>

          {isEditing && (
            <Button
              type="submit"
              disabled={isSaving}
              className="h-10 px-6 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg gap-2 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Salvar
                </>
              )}
            </Button>
          )}
        </div>
      </form>
    </Form>
  )
}

function HistoricoItem({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-3 py-1">
      <FileText className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
      <p className="text-gray-400 text-xs leading-relaxed">{text}</p>
    </div>
  )
}